import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetCostRecipe, useSaveCostRecipe, getGetCostRecipeQueryKey } from "@workspace/api-client-react";
import { costRecipeSnapshot, reconcileRecipeTypical, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { errorMessage } from "./cost-recipe-panel";

export interface RateConfirmationRequest {
  taskId: string;
  source: "default" | "custom";
  name: string;
  rateLow: string;
  rateTypical: string;
  rateHigh: string;
  changeReason?: string;
}

export function CostRecipeRateConfirmation({ request, onClose, onSaved, formatCurrency: fmt }: {
  request: RateConfirmationRequest;
  onClose: () => void;
  onSaved: () => void;
  formatCurrency: (value: string | number) => string;
}) {
  const qc = useQueryClient();
  const queryKey = getGetCostRecipeQueryKey(request.source, request.taskId);
  const query = useGetCostRecipe(request.source, request.taskId, { query: {
    queryKey, staleTime: 0, refetchOnMount: "always",
  } });
  const save = useSaveCostRecipe({ mutation: { onSuccess: state => {
    qc.setQueryData(queryKey, state);
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["/api/cost-library"] });
    qc.invalidateQueries({ predicate: q => typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/admin/cost-library/defaults") });
    onSaved();
  } } });
  const preview = useMemo(() => {
    if (!query.data) return null;
    try {
      if (!query.data.recipe) throw new Error("This task no longer has a breakdown. Close this confirmation and reload the task.");
      return { result: reconcileRecipeTypical(query.data.recipe, request.rateTypical), error: null };
    } catch (err) { return { result: null, error: err instanceof Error ? err.message : "Could not adjust the breakdown." }; }
  }, [query.data, request.rateTypical]);
  const before = query.data?.recipe;
  const after = preview?.result?.recipe;
  function allowances(recipe: typeof before | CostRecipe) {
    return recipe ? [
      { label: "Labour", value: recipe.labour_allowance },
      { label: "Plant", value: recipe.plant_allowance },
      ...(recipe.shape === "specification" ? [{ label: "Supply", value: recipe.supply_allowance ?? 0 }] : []),
      ...recipe.materials.map(m => ({ label: `${m.name} · per ${m.unit}`, value: m.unit_price })),
    ] : [];
  }
  const oldRows = allowances(before), newRows = allowances(after);
  const busy = query.isFetching || save.isPending;
  return <Dialog open onOpenChange={open => { if (!open && !save.isPending) onClose(); }}>
    <DialogContent className="max-w-xl" data-testid="dialog-confirm-typical">
      <DialogHeader>
        <DialogTitle>Update Typical and its breakdown?</DialogTitle>
        <DialogDescription>
          {request.name}: changing Typical to {fmt(request.rateTypical)} will also adjust labour, plant, supply allowances and material unit prices proportionally. Quantities, waste and specifications stay unchanged.
        </DialogDescription>
      </DialogHeader>
      {query.isLoading && <p className="text-sm">Loading the latest breakdown…</p>}
      {query.isError && <div className="space-y-2"><p className="text-sm text-destructive">{errorMessage(query.error)}</p>
        <Button variant="outline" onClick={() => query.refetch()}>Retry</Button></div>}
      {preview?.error && <p className="text-sm text-destructive" role="alert">{preview.error}</p>}
      {before && after && <div className="max-h-[50vh] overflow-auto rounded-md border">
        <table className="w-full text-sm" data-testid="table-confirm-typical">
          <thead><tr className="border-b"><th className="p-2 text-left">Rate / cost</th><th className="p-2 text-right">Before</th><th className="p-2 text-right">After</th></tr></thead>
          <tbody>
            {[
              { label: "Low", old: query.data!.rateLow, next: request.rateLow },
              { label: "Typical", old: query.data!.rateTypical, next: request.rateTypical },
              { label: "High", old: query.data!.rateHigh, next: request.rateHigh },
              ...oldRows.map((r, i) => ({ label: r.label, old: r.value, next: newRows[i]?.value })),
            ].map((r, i) => <tr key={i} className="border-b last:border-b-0">
              <td className="p-2 break-words">{r.label}</td><td className="p-2 text-right tabular-nums">{r.old != null ? fmt(r.old) : "—"}</td>
              <td className="p-2 text-right tabular-nums">{r.next != null ? fmt(r.next) : "—"}</td>
            </tr>)}
          </tbody>
        </table>
      </div>}
      {preview?.result?.roundingAdjustment && <p className="text-xs text-muted-foreground" data-testid="text-reprice-rounding">
        Includes a {fmt(preview.result.roundingAdjustment.amount)} rounding adjustment to {preview.result.roundingAdjustment.field.replace("_allowance", "")} so the breakdown matches Typical exactly.
      </p>}
      <p className="text-xs text-muted-foreground">Low and High will use the values entered in the rate editor.</p>
      {save.isError && <p className="text-sm text-destructive" role="alert">{errorMessage(save.error)}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" disabled={save.isPending} onClick={onClose}>Cancel</Button>
        <Button disabled={busy || !after || !query.data} data-testid="button-confirm-typical" onClick={() => {
          if (!query.data || !after) return;
          save.mutate({ source: request.source, id: request.taskId, data: { rateAdjustment: {
            rateLow: request.rateLow, rateTypical: request.rateTypical, rateHigh: request.rateHigh,
            expectedSnapshot: costRecipeSnapshot(query.data), changeReason: request.changeReason,
          } } });
        }}>{save.isPending ? "Saving…" : "Confirm and save"}</Button>
      </div>
    </DialogContent>
  </Dialog>;
}
